import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import { AppDispatch, GetState, RootState } from '../store'

export const TYPES = {
  LIVE: 'type.live',
  SMS: 'type.sms',
  EMAIL: 'type.email'
}

const TYPES_INVERSE = {
  [TYPES.LIVE]: 'Live Chat',
  [TYPES.SMS]: 'SMS',
  [TYPES.EMAIL]: 'Email'
}

export const getNameFromType = (type: string) => {
  if (!(type in TYPES_INVERSE)) {
    return ''
  }

  return TYPES_INVERSE[type]
}

export const getTypeFromName = type => {
  const entries = Object.entries(TYPES_INVERSE)
  const idx = entries.findIndex(([_, val]) => val === type)

  if (idx >= 0) {
    return entries[idx][0]
  }

  return ''
}

export const SEVERITY = {
  LOW: 'sevLow',
  NORMAL: 'sevNormal',
  HIGH: 'sevHigh'
}

type Alert = {
  id: string
  message: React.ReactNode[]
  severity: string
  hidden: boolean
  unique?: string
}

type MessageValuesType = {
  message: string
  emoji: string
  subject: string
  file: any[]
  allEmails: {}
  emails: string[]
  emailSet: boolean
  allPhones: {}
  phones: string[]
  phoneSet: boolean
  undoText: string
  isReplacedString: boolean
  hasUndoReplacedString: boolean
  type: string
  lastType: string
  isPrivate: boolean
  onTask: boolean
  alerts: Alert[]
}

const messageValues: MessageValuesType = {
  message: '',
  emoji: '',
  subject: '',
  file: [],
  allEmails: {},
  emails: [],
  emailSet: false,
  allPhones: {},
  phones: [],
  phoneSet: false,
  undoText: '',
  isReplacedString: false,
  hasUndoReplacedString: false,
  type: TYPES.LIVE,
  lastType: TYPES.LIVE,
  isPrivate: false,
  onTask: false,
  alerts: []
}

type ChatBoxState = {
  cache: { [x: string]: MessageValuesType }
  values: MessageValuesType
  currentID: string | null
  height: number
  alertsHidden: boolean
}

const initialState: ChatBoxState = {
  cache: {},
  values: {} as MessageValuesType,
  currentID: null,
  height: 0,
  alertsHidden: false
}

const chatBoxSlice = createSlice({
  name: 'chatBox',
  initialState,
  reducers: {
    switchSession: (state: ChatBoxState, action: PayloadAction<string>) => {
      const id = action.payload

      if (state.currentID) {
        state.cache[state.currentID] = { ...state.values }
      }

      if (id) {
        state.currentID = id
        state.values = state.cache[id] ?? { ...messageValues }
      } else {
        state.currentID = null
        state.values = {} as MessageValuesType
      }
    },

    changeType: (state: ChatBoxState, action: PayloadAction<string>) => {
      if (state.currentID) {
        state.values.type = action.payload
      }
    },

    startTaskCreate: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.onTask = true
      }
    },

    endTaskCreate: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.onTask = false
      }
    },

    togglePrivate: (state: ChatBoxState) => {
      if (state.currentID) {
        if (state.values.isPrivate) {
          state.values.isPrivate = false
          state.values.type = state.values.lastType
        } else {
          state.values.isPrivate = true
          state.values.lastType = state.values.type
          state.values.type = TYPES.LIVE
        }
      }
    },

    makePrivate: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.isPrivate = true
        state.values.lastType = state.values.type
        state.values.type = TYPES.LIVE
      }
    },

    setValue: (
      state: ChatBoxState,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      if (state.currentID) {
        const { key, value } = action.payload
        state.values[key] = value
      }
    },

    setChannelValue: (
      state: ChatBoxState,
      action: PayloadAction<{ isPhone: boolean; id: string }>
    ) => {
      const { isPhone, id } = action.payload
      if (state.currentID) {
        if (isPhone) {
          state.values.phones = [id]
          state.values.phoneSet = true
        } else {
          state.values.emails = [id]
          state.values.emailSet = true
        }

        // TODO: Uncomment this and delete the above to re-enable multi-channel sending
        // const list = isPhone ? state.values.phones : state.values.emails
        // if (list.includes(id)) {
        //   const removeIdx = list.indexOf(id)
        //   list.splice(removeIdx, 1)
        // } else {
        //   list.push(id)
        // }
        // state.values[isPhone ? 'phoneSet' : 'emailSet'] = true
      }
    },

    addAlert: (
      state,
      action: PayloadAction<{
        message: string | JSX.Element | (string | JSX.Element)[]
        unique?: string
        severity?: string
      }>
    ) => {
      if (state.currentID) {
        const { message, unique, severity = SEVERITY.NORMAL } = action.payload

        const toSave: Alert = {
          id: uuidv4(),
          message: Array.isArray(message) ? message : [message],
          severity,
          hidden: false
        }

        // Don't push if this unique key exists
        if (unique) {
          const idx = state.values.alerts.findIndex(
            alert => alert.unique === unique
          )

          if (idx >= 0) {
            return
          }

          toSave.unique = unique
        }

        state.values.alerts.push(toSave)
      }
    },

    hideAlert: (state: ChatBoxState, action: PayloadAction<string>) => {
      if (state.currentID) {
        const id = action.payload
        const idx = state.values.alerts.findIndex(alert => alert.id === id)
        if (idx >= 0) {
          state.values.alerts[idx].hidden = true
        }
      }
    },

    clearUniqueAlerts: (state: ChatBoxState, action: PayloadAction<string>) => {
      if (state.currentID) {
        const unique = action.payload
        state.values.alerts = state.values.alerts.filter(
          alert => !alert.unique || !alert.unique.startsWith(unique)
        )
      }
    },

    clearAlerts: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.alerts = []
      }
    },

    clearData: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.message = ''
        state.values.subject = ''
        state.values.file = []
      }
    },

    setHeight: (state: ChatBoxState, action: PayloadAction<number>) => {
      state.height = action.payload
    },

    insertEmoji: (state: ChatBoxState, action: PayloadAction<string>) => {
      if (state.currentID) {
        state.values.emoji = action.payload
      }
    },

    clearEmoji: (state: ChatBoxState) => {
      if (state.currentID) {
        state.values.emoji = ''
      }
    },

    setAlertsHidden: (state, { payload }) => {
      state.alertsHidden = payload
    }
  }
})

export const {
  switchSession,
  changeType,
  startTaskCreate,
  endTaskCreate,
  togglePrivate,
  makePrivate,
  setChannelValue,
  addAlert,
  hideAlert,
  setHeight,
  clearUniqueAlerts,
  clearAlerts,
  clearData,
  insertEmoji,
  clearEmoji,
  setAlertsHidden
} = chatBoxSlice.actions
const { setValue } = chatBoxSlice.actions

export const updateValue = (key: string, value: any) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    let newValue = value

    if (typeof value === 'function') {
      const oldValue = selectChatBox(getState()).values[key]
      newValue = value(oldValue)
    }

    dispatch(setValue({ key, value: newValue }))
  }
}

export const selectChatBox = (state: RootState) => state.chatBox

export default chatBoxSlice.reducer
